import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { getSectionColor } from './styles/theme';
import { buildIeltsDraftKey } from './utils/ieltsDraftStorage';
import { Base_URL } from './constants/api';
import MockTestShareButton from '../Common/MockTestShareButton';

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'borderColor' && prop !== 'sectionColor',
})(({ theme, borderColor, sectionColor }) => {
  // Create gradient from section's lighter color (left) to white (right)
  const gradientStart = sectionColor?.lighter || '#F8F2FF';
  const gradientEnd = '#FFFFFF';
  
  return {
    border: '1px solid #EDF2F7',
    borderLeft: `5px solid ${borderColor || '#996CFE'}`,
    borderRadius: '12px',
    padding: '16px 20px',
    width: '100%',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: `linear-gradient(to right, ${gradientStart} 0%, ${gradientEnd} 100%)`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    '&:hover': {
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)',
      borderColor: '#E2E8F0',
      transform: 'translateX(2px)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '12px',
    },
  };
});

const TestInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const TestTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#2D3748',
  marginBottom: '4px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const TestTime = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#718096',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const DifficultyChip = styled(Chip)(({ level }) => ({
  backgroundColor: 
    level === 'Easy' ? '#E6FFFA' : 
    level === 'Medium' || level === 'Med' ? '#FFFBEB' : 
    '#FFF5F5',
  color: 
    level === 'Easy' ? '#319795' : 
    level === 'Medium' || level === 'Med' ? '#D69E2E' : 
    '#E53E3E',
  height: '22px',
  fontSize: '11px',
  fontWeight: 800,
  borderRadius: '6px',
  marginRight: '12px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const StartButton = styled(Button)(({ theme, isLocked }) => ({
  backgroundColor: isLocked ? '#C6CBD9' : '#996CFE',
  color: 'white',
  borderRadius: '10px',
  padding: '6px 20px',
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  minWidth: '90px',
  height: '38px',
  boxShadow: isLocked ? 'none' : '0 4px 6px rgba(153, 108, 254, 0.15)',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  cursor: isLocked ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: isLocked ? '#C6CBD9' : '#7C4DFF',
    boxShadow: isLocked ? 'none' : '0 6px 12px rgba(153, 108, 254, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    padding: '4px 12px',
  },
}));

const ActionButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const SimulationBadge = styled(Chip)({
  height: '22px',
  borderRadius: '6px',
  backgroundColor: '#EEF7FF',
  color: '#175CD3',
  border: '1px solid #B2DDFF',
  fontSize: '10px',
  fontWeight: 800,
  fontFamily: "'Poppins', 'Inter', sans-serif",
  '& .MuiChip-label': {
    padding: '0 8px',
  },
});

const SectionalTestCard = ({
  title,
  time,
  difficulty,
  onStart,
  testType,
  selectedSection,
  testId,
  isLocked = false,
  loading = false,
  showSimulationBadge = false,
  sharePath,
}) => {
  const sectionColor = selectedSection ? getSectionColor(selectedSection) : getSectionColor('Listening');
  const borderColor = sectionColor.primary;
  const [hasDraft, setHasDraft] = useState(false);
  const draftKey = useMemo(() => buildIeltsDraftKey({
    scope: 'sectional',
    section: selectedSection,
    testId,
  }), [selectedSection, testId]);

  useEffect(() => {
    if (!draftKey) {
      setHasDraft(false);
      return;
    }
    try {
      setHasDraft(Boolean(window.localStorage.getItem(`swiftuni:ielts-draft:${draftKey}`)));
    } catch (_) {
      setHasDraft(false);
    }
  }, [draftKey]);
  
  const handleStartClick = () => {
    onStart(hasDraft ? 'resume' : undefined);
  };

  const clearDraft = async () => {
    if (!draftKey) return;
    try {
      window.localStorage.removeItem(`swiftuni:ielts-draft:${draftKey}`);
    } catch (_) {
      // Ignore browser cleanup failures.
    }

    try {
      await fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftKey,
          scope: 'sectional',
          section: selectedSection,
          testId,
        }),
      });
    } catch (_) {
      // If backend clear fails, local clear still lets this device start cleanly.
    }
    setHasDraft(false);
  };

  const handleRestartClick = async () => {
    await clearDraft();
    onStart(undefined);
  };
  
  return (
    <>
      <CardContainer borderColor={borderColor} sectionColor={sectionColor}>
        <TestInfo>
          <Box display="flex" alignItems="center" mb={0.5}>
            <TestTitle>{title}</TestTitle>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <DifficultyChip label={difficulty} level={difficulty} size="small" />
            <TestTime>{time}</TestTime>
            {showSimulationBadge && <SimulationBadge label="Exam Simulation" size="small" />}
          </Box>
        </TestInfo>
        <ActionButtons>
          <MockTestShareButton path={sharePath} />
          {hasDraft && !isLocked && !loading && (
            <StartButton variant="outlined" onClick={handleRestartClick} isLocked={false}>
              Restart
            </StartButton>
          )}
          <StartButton onClick={isLocked || loading ? undefined : handleStartClick} disabled={isLocked || loading} isLocked={isLocked}>
            {loading ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              isLocked ? 'Locked' : hasDraft ? 'Resume' : 'Start'
            )}
          </StartButton>
        </ActionButtons>
      </CardContainer>
    </>
  );
};

export default SectionalTestCard;
